/**
 * Live, searchable font picker for the "Параметры" tab's "Шрифт" field —
 * replaces the plain `<select>` (`SelectField` + `FONT_OPTIONS`) with a
 * trigger button + popover that previews every font in its own face.
 *
 * Renders each `FONTS` entry via `entry.cssVar` (the Google webfonts are
 * loaded globally in `globals.css`, so no per-row loading is needed).
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { FONTS, getFontEntry } from '@/lib/editor/fonts';
import { cn } from '@/lib/utils/cn';

interface FontPickerProps {
  value: string;
  onChange: (key: string) => void;
}

export function FontPicker({ value, onChange }: FontPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const current = getFontEntry(value);
  const filtered = FONTS.filter((f) => f.label.toLowerCase().includes(query.trim().toLowerCase()));

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-medium text-white outline-none transition focus:border-brand-500"
      >
        <span className="truncate" style={{ fontFamily: current.cssVar }}>
          {current.label}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-white/40 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute inset-x-0 top-full z-20 mt-1.5 max-h-72 w-full overflow-hidden rounded-lg border border-white/10 bg-[#171122] shadow-xl">
          <div className="relative border-b border-white/10">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск шрифта…"
              className="w-full bg-transparent py-2 pl-8 pr-2.5 text-xs text-white outline-none placeholder:text-white/30"
            />
          </div>

          <div data-lenis-prevent className="max-h-56 overflow-y-auto p-1">
            {filtered.length ? (
              filtered.map((entry) => {
                const selected = entry.key === current.key;
                return (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => {
                      onChange(entry.key);
                      close();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition',
                      selected ? 'bg-brand-500/15 ring-1 ring-inset ring-brand-500' : 'hover:bg-white/5'
                    )}
                  >
                    <span className="w-5 shrink-0 text-sm" style={{ fontFamily: entry.cssVar }}>
                      Aa
                    </span>
                    <span className="min-w-0 flex-1 truncate text-white/80" style={{ fontFamily: entry.cssVar }}>
                      {entry.label}
                    </span>
                    {selected && <Check className="h-3.5 w-3.5 shrink-0 text-brand-300" />}
                  </button>
                );
              })
            ) : (
              <p className="px-2.5 py-3 text-center text-[11px] text-white/40">Ничего не найдено</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
